/**
 * useTouchInput hook
 *
 * Handles touch input for mobile/tablet gameplay.
 * Maps touch positions to lanes and provides active lane state.
 */

import { useEffect, useRef, useState, useCallback } from 'react';

export interface TouchInputState {
  activeLanes: Set<number>;
  isTouchDevice: boolean;
  isTouchMode: boolean;
}

export interface UseTouchInputReturn extends TouchInputState {
  touchZonesRef: React.RefObject<HTMLDivElement>;
  setTouchMode: (enabled: boolean) => void;
}

/**
 * Detect if device supports touch
 */
function detectTouchDevice(): boolean {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    ((navigator as Navigator & { msMaxTouchPoints?: number }).msMaxTouchPoints ?? 0) > 0
  );
}

/**
 * Calculate which lane a touch corresponds to
 */
function getLaneFromTouch(
  touchX: number,
  containerRect: DOMRect,
  laneCount: number = 6
): number {
  const relativeX = touchX - containerRect.left;
  const laneWidth = containerRect.width / laneCount;
  const lane = Math.floor(relativeX / laneWidth);
  return Math.max(0, Math.min(laneCount - 1, lane));
}

export function useTouchInput(
  onLanePress: (lane: number) => void,
  enabled: boolean = true
): UseTouchInputReturn {
  const touchZonesRef = useRef<HTMLDivElement>(null);
  const [activeLanes, setActiveLanes] = useState<Set<number>>(new Set());
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isTouchMode, setIsTouchMode] = useState(false);
  
  // Track active touch identifiers per lane
  const touchMapRef = useRef<Map<number, number>>(new Map()); // touchId -> lane

  // Detect touch device on mount
  useEffect(() => {
    const hasTouch = detectTouchDevice();
    setIsTouchDevice(hasTouch);
    
    // Auto-enable touch mode on touch devices
    if (hasTouch) {
      const savedMode = localStorage.getItem('rhythmsense-input-mode');
      setIsTouchMode(savedMode ? savedMode === 'touch' : true);
    }
  }, []);

  // Persist touch mode preference
  const setTouchModePersisted = useCallback((enabled: boolean) => {
    setIsTouchMode(enabled);
    localStorage.setItem('rhythmsense-input-mode', enabled ? 'touch' : 'keyboard');
  }, []);

  // Handle touch start
  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!enabled || !isTouchMode) return;
      
      const container = touchZonesRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const newActiveLanes = new Set(activeLanes);

      for (let i = 0; i < e.changedTouches.length; i++) {
        const touch = e.changedTouches[i];
        const lane = getLaneFromTouch(touch.clientX, rect);
        
        // Track this touch for this lane
        touchMapRef.current.set(touch.identifier, lane);
        newActiveLanes.add(lane);
        
        // Trigger the lane press
        onLanePress(lane);
        
        // Haptic feedback if available
        if (navigator.vibrate) {
          navigator.vibrate(10);
        }
      }

      setActiveLanes(newActiveLanes);
    },
    [enabled, isTouchMode, activeLanes, onLanePress]
  );

  // Handle touch end
  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!enabled || !isTouchMode) return;

      const newActiveLanes = new Set(activeLanes);

      for (let i = 0; i < e.changedTouches.length; i++) {
        const touch = e.changedTouches[i];
        const lane = touchMapRef.current.get(touch.identifier);
        
        if (lane !== undefined) {
          touchMapRef.current.delete(touch.identifier);
          
          // Only remove lane if no other touches are using it
          let laneStillActive = false;
          touchMapRef.current.forEach((l) => {
            if (l === lane) laneStillActive = true;
          });
          
          if (!laneStillActive) {
            newActiveLanes.delete(lane);
          }
        }
      }

      setActiveLanes(newActiveLanes);
    },
    [enabled, isTouchMode, activeLanes]
  );

  // Handle touch cancel (e.g., call interrupt, system gesture)
  const handleTouchCancel = useCallback(
    (e: TouchEvent) => {
      if (!enabled || !isTouchMode) return;

      const newActiveLanes = new Set(activeLanes);

      for (let i = 0; i < e.changedTouches.length; i++) {
        const touch = e.changedTouches[i];
        const lane = touchMapRef.current.get(touch.identifier);
        
        if (lane !== undefined) {
          touchMapRef.current.delete(touch.identifier);
          
          let laneStillActive = false;
          touchMapRef.current.forEach((l) => {
            if (l === lane) laneStillActive = true;
          });
          
          if (!laneStillActive) {
            newActiveLanes.delete(lane);
          }
        }
      }

      setActiveLanes(newActiveLanes);
    },
    [enabled, isTouchMode, activeLanes]
  );

  // Attach touch event listeners
  useEffect(() => {
    const container = touchZonesRef.current;
    if (!container) return;

    // Use passive listeners for better scroll performance
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    // Prevent default touch behaviors that interfere with gameplay
    const preventDefault = (e: TouchEvent) => {
      if (isTouchMode && e.target === container) {
        e.preventDefault();
      }
    };
    
    container.addEventListener('touchmove', preventDefault, { passive: false });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchCancel);
      container.removeEventListener('touchmove', preventDefault);
    };
  }, [handleTouchStart, handleTouchEnd, handleTouchCancel, isTouchMode]);

  return {
    activeLanes,
    isTouchDevice,
    isTouchMode,
    touchZonesRef,
    setTouchMode: setTouchModePersisted,
  };
}
